
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useResumeDownload = () => {
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const cleanBase64 = (base64String: string) => {
    return base64String.replace(/\s/g, '');
  };

  const base64ToBlob = (base64String: string, mimeType: string) => {
    try {
      const cleanedBase64 = cleanBase64(base64String);
      const binaryString = atob(cleanedBase64);
      const bytes = new Uint8Array(binaryString.length);
      
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      return new Blob([bytes.buffer], { type: mimeType });
    } catch (error) {
      console.error("Error converting base64 to blob:", error);
      throw new Error("Failed to process the downloaded file");
    }
  };

  const downloadFile = (blob: Blob, fileName: string) => {
    try {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      throw new Error("Failed to download the file");
    }
  };

  const downloadAsPdf = async (resume: any) => {
    try {
      const { data, error } = await supabase.functions.invoke('convert-to-pdf', {
        body: { 
          content: resume.content,
          title: resume.title
        }
      });

      if (error) throw new Error(error.message);
      
      if (!data || !data.pdfContent) {
        throw new Error("No PDF content returned from the server");
      }
      
      const blob = base64ToBlob(data.pdfContent, 'application/pdf');
      downloadFile(blob, `${resume.title}.pdf`);
      
    } catch (error: any) {
      console.error("PDF download error:", error);
      throw new Error(`PDF generation failed: ${error.message}`);
    }
  };

  const downloadAsDocx = async (resume: any) => {
    try {
      const { data, error } = await supabase.functions.invoke('convert-to-docx', {
        body: { 
          content: resume.content,
          title: resume.title
        }
      });

      if (error) throw new Error(error.message);
      
      if (!data || !data.docxContent) {
        throw new Error("No DOCX content returned from the server");
      }
      
      const blob = base64ToBlob(
        data.docxContent, 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      );
      downloadFile(blob, `${resume.title}.docx`);
      
    } catch (error: any) {
      console.error("DOCX download error:", error);
      throw new Error(`DOCX generation failed: ${error.message}`);
    }
  };

  const handleFormatDownload = async (resume: any, format: "pdf" | "docx") => {
    if (!resume) return;
    
    setIsDownloading(true);
    try {
      switch (format) {
        case "pdf":
          await downloadAsPdf(resume);
          break;
        case "docx":
          await downloadAsDocx(resume);
          break;
      }
      
      toast({
        title: "Download Started",
        description: `Your resume is being downloaded in ${format.toUpperCase()} format.`
      });
    } catch (error: any) {
      console.error("Download error:", error);
      toast({
        title: "Download Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    handleFormatDownload,
    isDownloading
  };
};
